/* eslint max-lines-per-function: ["error", {max: 160}] -- flag-gated v1 time slots + v2 sections */
import { useEffect, useState } from "react";

import isNil from "lodash/isNil";

import { GroupClassRegistrationModalTestID } from "@/constants/TestID";
import type {
  RecommendedGroupClass,
  RecommendedGroupClassInstance,
} from "@/store/api/schedule/groupClasses";
import GroupClassDeclineReasonForm from "@/ui/components/groupClasses/series/GroupClassDeclineReasonForm";
import GroupRegistrationCard from "@/ui/components/groupClasses/series/GroupRegistrationCard";
import RecommendedGroupClassRegistrationActions from "@/ui/components/groupClasses/series/RecommendedGroupClassRegistrationActions";
import RecommendedGroupSectionsView from "@/ui/components/groupClasses/series/RecommendedGroupSectionsView";
import TimeSlotSelector from "@/ui/components/groupClasses/series/TimeSlotSelector";
import TherapistAssignmentHeader from "@/ui/components/home/TherapistAssignmentHeader";
import { Accordion, FAIconName } from "@/ui/core";
import BulletItem from "@/ui/core/BulletItem";
import { VStack } from "@/ui/layout";
import { DismissalMode } from "@/utils/recommendedGroupClass/storage.util";
import { joinTestIDs } from "@/utils/string.util";

enum ModalView {
  DeclineReasons = "DeclineReasons",
  TimeSlots = "TimeSlots",
}

interface RecommendedGroupClassModalContentProps extends ViewWithTestID {
  dismissalMode: DismissalMode;
  errorMessage?: string;
  faqs: {
    items: string[];
    title: string;
  };
  group: RecommendedGroupClass;
  isLoading?: boolean;
  onAccordionToggle?: (isOpen: boolean) => void;
  onDeclineSubmit: () => void;
  onNoneOfTheseTimesPress: () => void;
  onRegister: (args: {
    defaultInstanceId?: string;
    selectedInstance?: RecommendedGroupClassInstance;
  }) => void;
  // v2: called after a successful group-section registration from the sections view
  onRegistered?: () => void;
  onRemindMeLaterPress: () => void;
  // v2: patient the recommendation is for, used to fetch the group's sections
  patientId?: string;
  // v2: the logged-in user who registers (patient → self, support → self)
  registrantId?: string;
  subject: {
    isSupportUser: boolean;
    patientFirstName: string;
    therapist?: { id: string; name: string };
  };
}

const DismissalConfig: Record<
  DismissalMode,
  { icon: FAIconName; text: string }
> = {
  [DismissalMode.NoneOfTheseTimes]: {
    icon: FAIconName.Times,
    text: "None of these times work",
  },
  [DismissalMode.RemindMeLater]: {
    icon: FAIconName.Redo,
    text: "Remind me later",
  },
};

const RecommendedGroupClassModalContent = ({
  dismissalMode,
  errorMessage,
  faqs,
  group,
  isLoading,
  onAccordionToggle,
  onDeclineSubmit,
  onNoneOfTheseTimesPress,
  onRegister,
  onRegistered,
  onRemindMeLaterPress,
  patientId,
  registrantId,
  subject,
  testID,
}: RecommendedGroupClassModalContentProps) => {
  // v2 recommended groups carry a groupId + register per section; v1 uses flat time slots
  const isV2 = !isNil(group.groupId);
  const [currentView, setCurrentView] = useState<ModalView>(
    ModalView.TimeSlots,
  );
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>(
    group.instances[0]?.groupClassExternalId ?? "",
  );

  const defaultInstanceId = group.instances[0]?.groupClassExternalId;

  // queue advance keeps this component mounted; reset to time-slots so the next
  // recommendation doesn't open on the decline form (key: groupId v2 / instance id v1)
  useEffect(() => {
    setCurrentView(ModalView.TimeSlots);
  }, [group.groupId, defaultInstanceId]);

  useEffect(() => {
    if (isNil(defaultInstanceId)) {
      setSelectedInstanceId("");
      return;
    }

    const hasSelectedInstance = group.instances.some(
      (instance) => instance.groupClassExternalId === selectedInstanceId,
    );
    if (!hasSelectedInstance) {
      setSelectedInstanceId(defaultInstanceId);
    }
  }, [defaultInstanceId, group.instances, selectedInstanceId]);

  const selectedInstance = group.instances.find(
    (instance) => instance.groupClassExternalId === selectedInstanceId,
  );

  const { icon: dismissIcon, text: dismissText } =
    DismissalConfig[dismissalMode];

  const handleDismissPress = () => {
    if (dismissalMode === DismissalMode.NoneOfTheseTimes) {
      onNoneOfTheseTimesPress();
      setCurrentView(ModalView.DeclineReasons);
    } else {
      onRemindMeLaterPress();
    }
  };

  const handleBackToTimeSlots = () => {
    setCurrentView(ModalView.TimeSlots);
  };

  const renderFaqs = faqs.items.map((text) => (
    <BulletItem
      key={text}
      testID={joinTestIDs(testID, `bullet-${text}`)}
      text={text}
    />
  ));

  if (currentView === ModalView.DeclineReasons) {
    return (
      <GroupClassDeclineReasonForm
        onBack={handleBackToTimeSlots}
        onSubmit={onDeclineSubmit}
        testID={testID}
      />
    );
  }

  return (
    <VStack alignItems="center" gap={24} testID={testID}>
      <TherapistAssignmentHeader
        isSupportUser={subject.isSupportUser}
        patientFirstName={subject.patientFirstName}
        testID={testID}
        therapistName={subject.therapist?.name}
        therapistUserId={subject.therapist?.id}
      />

      <GroupRegistrationCard
        description={group.description}
        durationInWeeks={group.lengthInWeeks}
        hasCoreTag={group.isCore}
        iconName={group.iconName}
        testID={testID}
        title={group.title}
      />

      <Accordion
        onTriggerPress={onAccordionToggle}
        paddingVertical={18}
        testID={joinTestIDs(
          testID,
          GroupClassRegistrationModalTestID.accordion,
        )}
        title={faqs.title}
      >
        <VStack gap={8}>{renderFaqs}</VStack>
      </Accordion>

      {isV2 ? (
        <RecommendedGroupSectionsView
          dismissIcon={dismissIcon}
          dismissText={dismissText}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- isV2 guarantees groupId
          groupId={group.groupId!}
          onDismiss={handleDismissPress}
          onRegistered={onRegistered}
          patientId={patientId}
          registrantId={registrantId}
          testID={testID}
        />
      ) : (
        <>
          <TimeSlotSelector
            instances={group.instances}
            onSelect={setSelectedInstanceId}
            selectedInstanceId={selectedInstanceId}
            testID={joinTestIDs(
              testID,
              GroupClassRegistrationModalTestID.timeSlotSelector,
            )}
          />

          <RecommendedGroupClassRegistrationActions
            defaultInstanceId={defaultInstanceId}
            dismissIcon={dismissIcon}
            dismissText={dismissText}
            errorMessage={errorMessage}
            isLoading={isLoading}
            onDismiss={handleDismissPress}
            onRegister={onRegister}
            selectedInstance={selectedInstance}
            testID={testID}
          />
        </>
      )}
    </VStack>
  );
};

export type { RecommendedGroupClassModalContentProps };
export default RecommendedGroupClassModalContent;
